/* Menu: a popover with labeled groups (mockup NOTES "Overflow"). Opens on click; closes on an outside
   click, Esc, picking an item, or focus leaving it; Arrow keys, Home and End move between items.
   On touch or narrow windows it can open as a bottom sheet (`asSheet="auto"`). */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Check, Ellipsis, type LucideIcon } from 'lucide-react';
import { cx } from './cx';
import { CapBadge } from './basics';

export interface MenuItemSpec {
  id: string;
  label: ReactNode;
  icon?: LucideIcon;
  hint?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Renders a checkable item (menuitemcheckbox) with a check mark. */
  checked?: boolean;
  /** Keep the menu open after selecting (checkable items, toggles). */
  keepOpen?: boolean;
  cap?: string;
  /** Only shown when the menu opens as a sheet (phones), e.g. the strip actions. */
  sheetOnly?: boolean;
}

export interface MenuGroupSpec {
  id?: string;
  label: string;
  items?: MenuItemSpec[];
  /** Arbitrary content (segmented controls, toggles) rendered after the items. */
  content?: ReactNode;
  /** Items render as small pills in a wrapping row. */
  chips?: boolean;
  /** Spans both columns in the grid layout. */
  wide?: boolean;
  sheetOnly?: boolean;
}

export interface MenuTriggerProps {
  ref: RefObject<HTMLButtonElement | null>;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string;
  onClick: () => void;
}

export interface MenuProps {
  /** Accessible name of the trigger and of the menu. */
  label: string;
  groups: MenuGroupSpec[];
  /** Custom trigger; defaults to a "⋯" icon button. */
  trigger?: (props: MenuTriggerProps) => ReactNode;
  triggerIcon?: LucideIcon;
  /** Text after the icon (makes a text button such as "More"). */
  triggerText?: ReactNode;
  triggerClassName?: string;
  asSheet?: boolean | 'auto';
  layout?: 'list' | 'grid';
  align?: 'start' | 'end';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Group id to focus when the menu opens. */
  focusGroup?: string;
  className?: string;
}

const ITEM_SEL = '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"]';

export function isCoarseOrNarrow(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse = document.documentElement.dataset.pointer === 'coarse';
  return coarse || window.innerWidth < 600;
}

export function Menu({
  label, groups, trigger, triggerIcon: TriggerIcon = Ellipsis, triggerText, triggerClassName, asSheet = false,
  layout = 'list', align = 'end', open: openProp, onOpenChange, focusGroup, className,
}: MenuProps) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback((v: boolean) => { if (openProp === undefined) setOpenState(v); onOpenChange?.(v); }, [openProp, onOpenChange]);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const sheet = asSheet === 'auto' ? open && isCoarseOrNarrow() : asSheet;

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, [setOpen]);

  // Position next to the trigger, flipping up and clamping to the viewport.
  useLayoutEffect(() => {
    if (!open || sheet) { setPos(null); return; }
    const t = triggerRef.current, p = popRef.current;
    if (!t || !p) return;
    const r = t.getBoundingClientRect();
    const w = p.offsetWidth || 240, h = p.offsetHeight || 200;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = align === 'end' ? r.right - w : r.left;
    left = Math.max(8, Math.min(left, vw - w - 8));
    let top = r.bottom + 4;
    if (top + h > vh - 8 && r.top - h - 4 > 8) top = r.top - h - 4;
    top = Math.max(8, Math.min(top, vh - h - 8));
    setPos({ top, left });
  }, [open, sheet, align, groups]);

  // Focus the first item (or the requested group) on open.
  useEffect(() => {
    if (!open) return;
    const p = popRef.current;
    if (!p) return;
    const scope = (focusGroup && p.querySelector<HTMLElement>(`[data-group="${focusGroup}"]`)) || p;
    const first = Array.from(scope.querySelectorAll<HTMLElement>(ITEM_SEL)).find((el) => el.getAttribute('aria-disabled') !== 'true');
    (first ?? scope.querySelector<HTMLElement>('button,[tabindex]'))?.focus({ preventScroll: true });
    if (focusGroup) scope.scrollIntoView?.({ block: 'nearest' });
  }, [open, focusGroup]);

  // Outside click closes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent | MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, close]);

  const items = () => Array.from(popRef.current?.querySelectorAll<HTMLElement>(ITEM_SEL) ?? []).filter((el) => !el.closest('[hidden]'));

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
    const list = items();
    if (!list.length) return;
    const i = list.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === 'ArrowDown') next = i < 0 ? 0 : (i + 1) % list.length;
    else if (e.key === 'ArrowUp') next = i < 0 ? list.length - 1 : (i - 1 + list.length) % list.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = list.length - 1;
    if (next >= 0) { e.preventDefault(); list[next]!.focus(); }
  };

  const onBlur = (e: React.FocusEvent) => {
    const to = e.relatedTarget as Node | null;
    if (!to) return;
    if (popRef.current?.contains(to) || triggerRef.current?.contains(to)) return;
    close(false);
  };

  const select = (it: MenuItemSpec) => {
    if (it.disabled) return;
    it.onSelect?.();
    if (!it.keepOpen) close();
  };

  const triggerProps: MenuTriggerProps = {
    ref: triggerRef,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': menuId,
    onClick: () => setOpen(!open),
  };

  const visibleGroups = groups.filter((g) => sheet || !g.sheetOnly);

  const pop = open ? (
    <>
      {sheet && <div className="menu-scrim" aria-hidden="true" onPointerDown={() => close(false)} />}
      <div
        ref={popRef}
        id={menuId}
        role="menu"
        aria-label={label}
        className={cx('menu-pop', layout === 'grid' && !sheet && 'grid2col', sheet && 'as-sheet', className)}
        style={sheet ? undefined : { top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      >
        {visibleGroups.map((g, gi) => (
          <div key={g.id ?? g.label ?? gi} className={cx('menu-grp', g.wide && 'wide')} role="group" aria-label={g.label} data-group={g.id}>
            <div className="menu-h" aria-hidden="true">{g.label}</div>
            {g.items && (
              <div className={g.chips ? 'menu-chips' : undefined} style={g.chips ? undefined : { display: 'contents' }}>
                {g.items.filter((it) => sheet || !it.sheetOnly).map((it) => {
                  const Icon = it.icon;
                  const checkable = it.checked !== undefined;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      tabIndex={-1}
                      role={checkable ? 'menuitemcheckbox' : 'menuitem'}
                      aria-checked={checkable ? it.checked : undefined}
                      aria-disabled={it.disabled || undefined}
                      className={cx('menu-item', it.danger && 'danger')}
                      data-cap={it.cap}
                      onClick={() => select(it)}
                    >
                      {Icon && <Icon size={16} aria-hidden="true" />}
                      <span>{it.label}</span>
                      {it.cap && <CapBadge id={it.cap} />}
                      {it.hint && <span className="mi-hint">{it.hint}</span>}
                      {checkable && <Check size={14} className="mi-check" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            )}
            {g.content && <div className="menu-pad">{g.content}</div>}
          </div>
        ))}
      </div>
    </>
  ) : null;

  return (
    <>
      {trigger ? trigger(triggerProps) : (
        <button
          type="button"
          ref={triggerRef}
          className={cx(triggerText ? 'btn ghost sm' : 'icon-btn', triggerClassName)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label={triggerText ? undefined : label}
          title={triggerText ? undefined : label}
          onClick={triggerProps.onClick}
        >
          <TriggerIcon size={18} aria-hidden="true" />
          {triggerText}
        </button>
      )}
      {pop && (typeof document !== 'undefined' ? createPortal(pop, document.body) : pop)}
    </>
  );
}
